import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { discussionService } from '@/services/discussion.service';
import type { ForumDiscussionDto } from '@/types/discussion.types';
import { getNetLikes } from '@/types/discussion.types';
import toast from 'react-hot-toast';

interface ReactionButtonsProps {
  discussion: ForumDiscussionDto;
  threadId: string;
  onReactionUpdate: (updated: ForumDiscussionDto) => void;
}

export default function ReactionButtons({ discussion, threadId, onReactionUpdate }: ReactionButtonsProps) {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleLike = async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      let updated: ForumDiscussionDto;

      if (discussion.user_liked) {
        // Already liked, so unlike
        updated = await discussionService.unlikeDiscussion(threadId, discussion._id);
      } else {
        // Not liked, so like (and remove dislike if exists)
        updated = await discussionService.likeDiscussion(threadId, discussion._id);
      }

      onReactionUpdate(updated);
    } catch (error) {
      console.error('Error liking discussion:', error);
      toast.error('Failed to update reaction');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDislike = async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      let updated: ForumDiscussionDto;

      if (discussion.user_disliked) {
        // Already disliked, so undislike
        updated = await discussionService.undislikeDiscussion(threadId, discussion._id);
      } else {
        // Not disliked, so dislike (and remove like if exists)
        updated = await discussionService.dislikeDiscussion(threadId, discussion._id);
      }

      onReactionUpdate(updated);
    } catch (error) {
      toast.error('Failed to update reaction');
    } finally {
      setIsProcessing(false);
    }
  };

  const netLikes = getNetLikes(discussion);

  return (
    <div className="flex items-center gap-2">
      {/* Like Button */}
      <button
        onClick={handleLike}
        disabled={isProcessing}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all ${
          discussion.user_liked
            ? 'bg-indigo-100 text-indigo-700'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        title={discussion.user_liked ? 'Unlike' : 'Like'}
      >
        <ThumbsUp className={`w-3.5 h-3.5 ${discussion.user_liked ? 'fill-current' : ''}`} />
        <span>{discussion.like_count}</span>
      </button>

      {/* Dislike Button */}
      <button
        onClick={handleDislike}
        disabled={isProcessing}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all ${
          discussion.user_disliked
            ? 'bg-red-100 text-red-700'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        title={discussion.user_disliked ? 'Undislike' : 'Dislike'}
      >
        <ThumbsDown className={`w-3.5 h-3.5 ${discussion.user_disliked ? 'fill-current' : ''}`} />
        <span>{discussion.dislike_count}</span>
      </button>

      {/* Net Likes Display (Optional) */}
      {netLikes !== 0 && (
        <span className={`text-xs font-medium ${netLikes > 0 ? 'text-indigo-600' : 'text-red-600'}`}>
          {netLikes > 0 ? `+${netLikes}` : netLikes}
        </span>
      )}
    </div>
  );
}
